import { ButtonProps } from '@chakra-ui/react'

type CTA = {
  label?: string
  href?: string
  buttonProps?: ButtonProps
}

export type MemberMatchingLandingVariant = {
  key: string
  title1?: string
  title2?: string
  featuredPeopleHeading?: string
  featuredPeople?: Array<{
    fullName?: string
    title?: string
    pictureUrl?: string
    bio?: string
    skills?: string[]
  }>
  alternatingBlocks?: Array<{
    title?: string
    body?: string
    imageUrl?: string
    imagePlacement?: 'left' | 'right'
    cta?: CTA
  }>
}

export const variants = {
  default: {
    key: 'default',
    title1: 'Build your Startup Dream Team',
    title2: 'Find Startup-Obsessed Co-Founders, Experts, Peers, and Talent',
    featuredPeopleHeading: 'Featured Profiles',
    featuredPeople: [
      {
        fullName: 'Andrew Crefeld',
        title: 'Co-Founder',
        pictureUrl: require('@public/assets/graphics/avatars/matching/andrew-crefeld.jpg')?.default?.src,
        bio: 'Launched HouseLens in 2008 and was acquired in 2020. Starting a new company (on the side) called RentOrShare.IT.',
        skills: ['Fundraising', 'MVP Development', 'Hiring', 'Partnerships'],
      },
      {
        fullName: 'Meg Schlabs',
        title: 'Graphic Design',
        pictureUrl: require('@public/assets/graphics/avatars/matching/meg-schlabs.jpg')?.default?.src,
        bio: 'Helping companies reach their audience through branding, graphics and web design since 2011.',
        skills: ['Web Design', 'UX Research', 'Brand Design', 'Illustration'],
      },

      {
        fullName: 'Greg Welch',
        title: 'Advisor',
        pictureUrl: require('@public/assets/graphics/avatars/matching/greg-welch.jpg')?.default?.src,
        bio: 'Tech-focused Chief Executive with a proven track record of founding, building, and operating companies across multiple marketplaces.\n',
        skills: ['Fundraising', 'Revenue Growth', 'Hiring', 'Sales'],
      },
    ],
    alternatingBlocks: [
      {
        title: 'AI Precision Matching',
        body: "Our AI instantly matches your exact startup needs with members perfectly scored to fit your requirements. We'll take hundreds of thousands of candidates and give you the absolute best fit to start with.",
        imageUrl: require('@public/assets/graphics/matching/ai-precision-matching.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Start Matching',
        },
      },
      {
        title: 'Co-Founders',
        body: 'Add the perfect compliment to your skill set with a Co-Founder who can offer technical, sales, marketing or fundraising skills. Give yourself tons of options to find the best possible long term fit.',
        imageUrl: require('@public/assets/graphics/matching/test-first-hire-later.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Co-Founder',
          href: '/members/matching/start?roles=cofounder',
        },
      },
      {
        title: 'Expert Advice',
        body: "Tap into deep expertise in everything from Customer Acquisition to Technical Development to Legal issues from fellow Founders who have already figured out what you're wrestling with.",
        imageUrl: require('@public/assets/graphics/matching/expert-advice.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Meet an Advisor',
          href: '/members/matching/start?roles=advice',
        },
      },
      {
        title: 'Founder Peers',
        body: 'Surround yourself with super-smart, like-minded Founders who really "get it" and can help you grow your business. Expand your business network while also building some cool new friendships along the way.',
        imageUrl: require('@public/assets/graphics/matching/founder-peers.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Meet Your Peers',
          href: '/members/matching/start?roles=peer',
        },
      },
      {
        title: 'Startup Talent',
        body: 'Find the people you need to grow, from Developers to Designers to Marketing Managers, who are used to being paid with startup-style compensation and working on world-changing ideas.',
        imageUrl: require('@public/assets/graphics/matching/startup-talent.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find Talent',
          href: '/members/matching/start?roles=talent',
        },
      },
    ],
  },
  'talent-developer': {
    key: 'talent-developer',
    title1: 'Find Startup Developers for your Product',
    title2: 'Product-focused Developers who work on Startup Budgets',
    featuredPeopleHeading: 'Featured Profiles',
    featuredPeople: [
      {
        fullName: 'Andrew Crefeld',
        title: 'Co-Founder',
        pictureUrl: require('@public/assets/graphics/avatars/matching/andrew-crefeld.jpg')?.default?.src,
        bio: 'Launched HouseLens in 2008 and was acquired in 2020. Starting a new company (on the side) called RentOrShare.IT.',
        skills: ['Fundraising', 'MVP Development', 'Hiring', 'Partnerships'],
      },
      {
        fullName: 'Meg Schlabs',
        title: 'Graphic Design',
        pictureUrl: require('@public/assets/graphics/avatars/matching/meg-schlabs.jpg')?.default?.src,
        bio: 'Helping companies reach their audience through branding, graphics and web design since 2011.',
        skills: ['Web Design', 'UX Research', 'Brand Design', 'Illustration'],
      },

      {
        fullName: 'Greg Welch',
        title: 'Advisor',
        pictureUrl: require('@public/assets/graphics/avatars/matching/greg-welch.jpg')?.default?.src,
        bio: 'Tech-focused Chief Executive with a proven track record of founding, building, and operating companies across multiple marketplaces.\n',
        skills: ['Fundraising', 'Revenue Growth', 'Hiring', 'Sales'],
      },
    ],
    alternatingBlocks: [
      {
        title: 'Budget Friendly',
        body: 'Build your MVP without breaking the bank. Connect with devs who thrive in bootstrapped, equity-friendly startups.',
        imageUrl: require('@public/assets/graphics/matching/budget-friendly.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find a Developer',
          href: '/matching/talent-developer/start?roles=talent&skills=Engineering,Software+Engineering,Software+Development',
        },
      },
      {
        title: 'Test First, Hire Later',
        body: "Start off on a project basis so you can get to know each other, and then ramp up your commitment when you feel you've found the right person.",
        imageUrl: require('@public/assets/graphics/matching/test-first-hire-later.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Developer',
          href: '/matching/talent-developer/start?roles=talent&skills=Engineering,Software+Engineering,Software+Development',
        },
      },
      {
        title: 'Co-Founder Potential',
        body: 'Meet developers ready to grow with you. Find tech partners eager to join your founding team long-term when you feel the time is right.',
        imageUrl: require('@public/assets/graphics/matching/co-founder-potential.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Meet a Technical Co-Founder',
          href: '/matching/talent-developer/start?roles=cofounder&skills=Engineering,Software+Engineering,Software+Development',
        },
      },
      {
        title: 'No Placement Fees',
        body: 'Hire without hidden costs. Connect directly with talent—zero recruiting fees, maximum startup value.',
        imageUrl: require('@public/assets/graphics/matching/no-placement-fees.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Developer',
          href: '/matching/talent-developer/start?roles=talent&skills=Engineering,Software+Engineering,Software+Development',
        },
      },
      {
        title: 'AI Precision Matching',
        body: 'Our AI instantly matches your exact startup needs with developers perfectly scored to fit your team.',
        imageUrl: require('@public/assets/graphics/matching/startup-talent.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find a Developer',
          href: '/matching/talent-developer/start?roles=talent&skills=Engineering,Software+Engineering,Developer',
        },
      },
    ],
  },
  'talent-marketing': {
    key: 'talent-marketing',
    title1: 'Find Startup Marketing and Sales Gurus',
    title2: 'Acquire Customers 10x Faster with Experts who "Get Growth"',
    featuredPeopleHeading: 'Featured Profiles',
    featuredPeople: [
      {
        fullName: 'Andrew Crefeld',
        title: 'Co-Founder',
        pictureUrl: require('@public/assets/graphics/avatars/matching/andrew-crefeld.jpg')?.default?.src,
        bio: 'Launched HouseLens in 2008 and was acquired in 2020. Starting a new company (on the side) called RentOrShare.IT.',
        skills: ['Fundraising', 'MVP Development', 'Hiring', 'Partnerships'],
      },
      {
        fullName: 'Meg Schlabs',
        title: 'Graphic Design',
        pictureUrl: require('@public/assets/graphics/avatars/matching/meg-schlabs.jpg')?.default?.src,
        bio: 'Helping companies reach their audience through branding, graphics and web design since 2011.',
        skills: ['Web Design', 'UX Research', 'Brand Design', 'Illustration'],
      },

      {
        fullName: 'Greg Welch',
        title: 'Advisor',
        pictureUrl: require('@public/assets/graphics/avatars/matching/greg-welch.jpg')?.default?.src,
        bio: 'Tech-focused Chief Executive with a proven track record of founding, building, and operating companies across multiple marketplaces.\n',
        skills: ['Fundraising', 'Revenue Growth', 'Hiring', 'Sales'],
      },
    ],
    alternatingBlocks: [
      {
        title: 'Budget Friendly',
        body: 'Accelerate your marketing efforts without breaking the bank. Connect with marketing gurus who thrive in bootstrapped, equity-friendly startups.',
        imageUrl: require('@public/assets/graphics/matching/budget-friendly.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find a Marketer',
          href: '/matching/talent-marketing/start?roles=talent&skills=Marketing,Sales,Advertising',
        },
      },
      {
        title: 'Test First, Hire Later',
        body: "Start off on a project basis so you can get to know each other, and then ramp up your commitment when you feel you've found the right person.",
        imageUrl: require('@public/assets/graphics/matching/test-first-hire-later.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Marketer',
          href: '/matching/talent-marketing/start?roles=talent&skills=Marketing,Sales,Advertising',
        },
      },
      {
        title: 'Co-Founder Potential',
        body: 'Meet marketers ready to grow with you. Find marketing, sales, and advertising partners who are eager to join your founding team long-term when you feel the time is right.',
        imageUrl: require('@public/assets/graphics/matching/co-founder-potential.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Meet a Marketing Co-Founder',
          href: '/matching/talent-marketing/start?cofounder=talent&skills=Marketing,Sales,Advertising',
        },
      },
      {
        title: 'No Placement Fees',
        body: 'Hire without hidden costs. Connect directly with talent—zero recruiting fees, maximum startup value.',
        imageUrl: require('@public/assets/graphics/matching/no-placement-fees.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Marketer',
          href: '/matching/talent-marketing/start?roles=talent&skills=Marketing,Sales,Advertising',
        },
      },
      {
        title: 'AI Precision Matching',
        body: 'Our AI instantly matches your exact startup needs with marketers perfectly scored to fit your team.',
        imageUrl: require('@public/assets/graphics/matching/startup-talent.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Start Matching',
          href: '/matching/talent-marketing/start',
        },
      },
    ],
  },
  ['cofounder']: {
    key: 'cofounder',
    title1: 'Find a Ridiculously Qualified Co-Founder',
    title2: 'Build way faster with someone who compliments your skills and lightens the load.',
    featuredPeopleHeading: 'Featured Profiles',
    featuredPeople: [
      {
        fullName: 'Andrew Crefeld',
        title: 'Co-Founder',
        pictureUrl: require('@public/assets/graphics/avatars/matching/andrew-crefeld.jpg')?.default?.src,
        bio: 'Launched HouseLens in 2008 and was acquired in 2020. Starting a new company (on the side) called RentOrShare.IT.',
        skills: ['Fundraising', 'MVP Development', 'Hiring', 'Partnerships'],
      },
      {
        fullName: 'Meg Schlabs',
        title: 'Graphic Design',
        pictureUrl: require('@public/assets/graphics/avatars/matching/meg-schlabs.jpg')?.default?.src,
        bio: 'Helping companies reach their audience through branding, graphics and web design since 2011.',
        skills: ['Web Design', 'UX Research', 'Brand Design', 'Illustration'],
      },

      {
        fullName: 'Greg Welch',
        title: 'Advisor',
        pictureUrl: require('@public/assets/graphics/avatars/matching/greg-welch.jpg')?.default?.src,
        bio: 'Tech-focused Chief Executive with a proven track record of founding, building, and operating companies across multiple marketplaces.\n',
        skills: ['Fundraising', 'Revenue Growth', 'Hiring', 'Sales'],
      },
    ],
    alternatingBlocks: [
      {
        title: 'Direct Intros',
        body: 'We allow you to connect directly with promising Co-Founders so you can start your journey as you want.',
        imageUrl: require('@public/assets/illustration/matching/cofounder-1.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find a Co-Founder',
          href: '/matching/cofounder/start?roles=cofounder',
        },
      },
      {
        title: "Don't Go It Alone",
        body: "Building a startup solo sucks. Find someone who's just as driven as you are, so you can share the wins, losses...and late-night panic sessions.",
        imageUrl: require('@public/assets/illustration/matching/cofounder-2.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Co-Founder',
          href: '/matching/cofounder/start?roles=cofounder',
        },
      },
      {
        title: 'Tired of Busted Searches?',
        body: "Stop torturing LinkedIn or social media to find a Co-Founder - they aren't designed for startups. We've got potential Founders specifically looking for the risk and reward of a startup.",
        imageUrl: require('@public/assets/illustration/matching/cofounder-3.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find a Co-Founder',
          href: '/matching/cofounder/start?roles=cofounder',
        },
      },
      {
        title: 'Expand your Choices',
        body: "Stop settling for whoever's in your local startup meetup. Instantly access hundreds of serious founders across roles, industries, and experience levels.",
        imageUrl: require('@public/assets/illustration/matching/cofounder-4.png'),
        imagePlacement: 'left',
        cta: {
          label: 'Find a Co-Founder',
          href: '/matching/cofounder/start?roles=cofounder',
        },
      },
      {
        title: 'Compliment your Skill Set',
        body: 'Need someone with fundraising experience? How about a technical Co-Founder or someone who knows how to reel in customers? Find someone that perfectly compliments your skill set.',
        imageUrl: require('@public/assets/illustration/matching/cofounder-5.png'),
        imagePlacement: 'right',
        cta: {
          label: 'Find a Co-Founder',
          href: '/matching/cofounder/start?roles=cofounder',
        },
      },
    ],
  },
}
